import React, { useEffect, useState } from 'react';
import { Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { oapi, odownload, fmtGameDate } from '@/lib/officialsApi';
import { DownloadMenu, Loading, MetricCard, Money, Panel, PayStatusBadge } from '../payments/PayBits';

const CARDS = [['upcoming', 'Upcoming', 'Scheduled games', 'text-slate-700'], ['completed', 'Completed', 'Awaiting approval', 'text-sky-700'],
  ['approved', 'Approved', 'Includes paid', 'text-indigo-700'], ['paid', 'Paid to you', '', 'text-emerald-700'], ['outstanding', 'Owed to you', 'Approved, not yet paid', 'text-red-600']];
const LABEL = { pending_verification: 'Awaiting verification', earned: 'Awaiting approval' };

const LineRow = ({ l, currency }) => (
  <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 border-t first:border-t-0" data-testid={`earning-line-${l.id}`}>
    <span className="w-24 text-xs font-semibold text-slate-500">{fmtGameDate(l.date)}</span>
    <div className="flex-1 min-w-[160px]">
      <div className="text-sm font-semibold text-slate-800">{l.title}</div>
      <div className="text-xs text-slate-500">{[l.role_label, l.subtitle].filter(Boolean).join(' · ')}</div>
    </div>
    <PayStatusBadge status={l.status} label={LABEL[l.status]} testId={`earning-status-${l.id}`} />
    <Money minor={l.amount_minor} currency={currency} className="w-20 text-right font-bold text-slate-900" testId={`earning-amount-${l.id}`} />
  </li>
);

const PaymentRow = ({ p, currency }) => (
  <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 border-t first:border-t-0" data-testid={`earning-payment-${p.id}`}>
    <span className="w-24 text-xs font-semibold text-slate-500">{fmtGameDate(p.paid_on)}</span>
    <div className="flex-1 min-w-[160px] text-sm text-slate-700">{p.method}{p.reference ? ` · Ref ${p.reference}` : ''}
      <span className="text-xs text-slate-400"> · {p.games_covered} game{p.games_covered === 1 ? '' : 's'}</span></div>
    {p.status === 'reversed' && <span className="text-[11px] font-bold text-red-700 bg-red-50 rounded-full px-2 py-0.5">Reversed</span>}
    <Money minor={p.amount_minor} currency={currency} className={`w-20 text-right font-bold ${p.status === 'reversed' ? 'line-through text-slate-400' : 'text-emerald-700'}`} />
  </li>
);

const Empty = ({ text, testId }) => <p className="py-10 text-center text-sm text-slate-500" data-testid={testId}>{text}</p>;

export const MyEarnings = () => {
  const [d, setD] = useState(null);
  const [i, setI] = useState(0);
  useEffect(() => { oapi('/officials/portal/earnings').then(setD).catch(e => toast.error(e.message)); }, []);
  if (!d) return <Loading />;
  if (!d.orgs.length) return <Empty text="No earnings yet." testId="earnings-empty" />;
  const o = d.orgs[Math.min(i, d.orgs.length - 1)];
  const download = (format) => odownload('/officials/portal/earnings/statement', { official_id: o.official_id, format }, `payment_statement.${format}`)
    .catch(e => toast.error(e.message));
  return (
    <div className="space-y-5" data-testid="portal-earnings">
      <div className="flex flex-wrap items-center gap-2">
        {d.orgs.length > 1 && d.orgs.map((x, j) => (
          <button key={x.official_id} type="button" onClick={() => setI(j)} data-testid={`earnings-org-${j}`}
            className={`px-3 py-1.5 rounded-full border text-sm font-semibold transition-colors ${j === i ? 'bg-[#00247D] border-[#00247D] text-white' : 'bg-white text-slate-600'}`}>{x.org_name}</button>
        ))}
        <p className="text-xs text-slate-500 flex items-center gap-1.5 mr-auto"><Wallet className="w-3.5 h-3.5" />Fees are set by {o.org_name}. Ask your assigner if something looks off.</p>
        <DownloadMenu onPick={download} label="Statement" testId="earnings-statement" formats={['pdf', 'csv']} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {CARDS.map(([k, l, sub, tone]) => <MetricCard key={k} label={l} minor={o.totals[k]} currency={o.currency} tone={tone} sub={sub} testId={`earnings-total-${k}`} />)}
      </div>
      <Panel title="Games & adjustments" desc="Newest first" testId="earnings-lines">
        <ul>{o.lines.map(l => <LineRow key={l.id} l={l} currency={o.currency} />)}</ul>
        {!o.lines.length && <Empty text="Your game fees show up here once you're assigned." testId="earnings-lines-empty" />}
      </Panel>
      <Panel title="Payments received" testId="earnings-payments">
        <ul>{o.payments.map(p => <PaymentRow key={p.id} p={p} currency={o.currency} />)}</ul>
        {!o.payments.length && <Empty text="No payments yet." testId="earnings-payments-empty" />}
      </Panel>
    </div>
  );
};
